import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Box, useTheme } from '@mui/material';

import { UseMutateFunction } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { UserInfo } from '@/api/data-contracts';
import { ReactComponent as DeleteIcon } from '@/assets/icons/DeleteRounded.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/EditRounded.svg';
import { ReactComponent as MarkAsTaskIcon } from '@/assets/icons/MarkAsTaskRounded.svg';
import { ReactComponent as ReplyIcon } from '@/assets/icons/ReplyRounded.svg';
import CommentOrTaskForm from '@/features/comments/ui/CommentOrTaskForm';
import AuthorChips from '@/features/comments/ui/Comments/AuthorChips';
import {
  BodyText,
  CommentHeader,
  ContentFooter,
  ContentSection,
  FooterButton,
  FooterButtonText,
  HeaderSection,
  TimeAgo,
  UserName,
} from '@/features/comments/ui/Comments/Comment.styles';
import { CommentWithUnread } from '@/features/comments/ui/Comments/commentUtils';
import Avatar from '@/shared/ui/Avatar';
import ConfirmationDialog from '@/shared/ui/ConfirmationDialog';

import { Container } from './CommentReply.styles';
import useAuthorInfo from './useAuthorInfo';

const ICON_STYLE = { width: '11px', height: '11px', flexShrink: 0 } as const;

const isImageFile = (name: string | null | undefined): boolean => {
  if (!name) return false;
  return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(name);
};

const CommentReplyAttachmentThumb: FC<{
  file: { objectId?: string | null; name?: string | null };
  resourceId: string;
  downloadCommentFile: (resourceId: string, documentId: string) => Promise<Blob>;
}> = ({ file, resourceId, downloadCommentFile }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!file.objectId) return;
    let objectUrl: string | null = null;
    downloadCommentFile(resourceId, file.objectId)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.objectId, resourceId]);

  if (!url) return null;

  if (isImageFile(file.name)) {
    return (
      <>
        <Box
          component="img"
          src={url}
          alt={file.name ?? ''}
          onClick={() => setOpen(true)}
          sx={{
            width: 64,
            height: 64,
            objectFit: 'cover',
            borderRadius: '6px',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        />
        {open && (
          <Box
            onClick={() => setOpen(false)}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={url}
              alt={file.name ?? ''}
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <Box
      component="a"
      href={url}
      download={file.name ?? 'file'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.5,
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'grey.200',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'text.primary',
        fontSize: '0.75rem',
        maxWidth: 200,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': { borderColor: 'grey.400', textDecoration: 'underline' },
      }}
    >
      {file.name}
    </Box>
  );
};

interface Props {
  reply: CommentWithUnread;
  commentsThreadId?: string;
  onTaskSave?: (content: string, priority?: 'LOW' | 'MEDIUM' | 'HIGH') => void;
  onMarkAsTask?: (message: string) => void;
  deleteComment?: UseMutateFunction<void, unknown, string, unknown>;
  onCommentSave: (params: {
    message: string;
    files?: File[];
    commentId?: string;
    parentId?: string;
    edit?: boolean;
  }) => void;
  allUsers: UserInfo[];
  currentUserId: string;
  setReplyFormVisible?: Dispatch<SetStateAction<boolean>>;
  highlighted?: boolean;
  downloadCommentFile?: (resourceId: string, documentId: string) => Promise<Blob>;
}

const CommentReply: FC<Props> = ({
  reply,
  commentsThreadId,
  onTaskSave,
  onMarkAsTask,
  deleteComment,
  onCommentSave,
  allUsers,
  currentUserId,
  setReplyFormVisible,
  highlighted = false,
  downloadCommentFile,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { avatarColor, authorRole, platformChipInfo } = useAuthorInfo({
    authorId: reply.author.id,
    authorUserType: reply.author.userType,
    authorPosition: reply.author.position,
    allUsers,
  });

  const [isHighlighted, setIsHighlighted] = useState(highlighted);
  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlighted) return;
    setIsHighlighted(true);
    const rafId = requestAnimationFrame(() => {
      const el = highlightRef.current;
      if (!el) return;
      let container: HTMLElement | null = el.parentElement;
      while (container) {
        const { overflowY } = window.getComputedStyle(container);
        if (overflowY === 'auto' || overflowY === 'scroll') break;
        container = container.parentElement;
      }
      if (container) {
        const elTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTo({
          top: container.scrollTop + elTop - container.clientHeight / 2 + el.clientHeight / 2,
          behavior: 'smooth',
        });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    const timer = setTimeout(() => setIsHighlighted(false), 3000);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [highlighted]);

  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const isValidJsonWithOpsFormat = useMemo((): boolean => {
    try {
      const parsed = JSON.parse(reply.message);

      return !!(
        typeof parsed === 'object' &&
        parsed !== null &&
        'ops' in parsed &&
        Array.isArray(parsed.ops)
      );
    } catch (e) {
      return false;
    }
  }, [reply.message]);

  const author = useMemo(() => reply.author, [reply.author]);

  const renderBody = useCallback(() => {
    if (!isValidJsonWithOpsFormat) {
      return reply.message;
    }

    const ops = JSON.parse(reply.message).ops;

    return ops.map((op: any, index: number) => {
      const isLast = index === ops.length - 1;
      const text =
        isLast && typeof op.insert === 'string' ? op.insert.replace(/\n+$/, '') : op.insert;

      if (!text) return null;

      if (!op.attributes && !op.insert?.mention) {
        return (
          <span key={index} style={{ whiteSpace: 'pre-line' }}>
            {text}
          </span>
        );
      }

      if (op.insert?.mention) {
        return <span key={index}>{op.insert.mention.value}</span>;
      }

      return (
        <span
          key={index}
          style={{
            textDecoration:
              op.attributes?.strike || op.attributes?.underline
                ? op.attributes?.strike
                  ? 'line-through'
                  : 'underline'
                : 'none',
            fontStyle: op.attributes?.italic ? 'italic' : 'normal',
            fontWeight: op.attributes?.bold ? 'bold' : 'normal',
            whiteSpace: 'pre-line',
          }}
        >
          {text}
        </span>
      );
    });
  }, [isValidJsonWithOpsFormat, reply.message]);

  const renderButtons = useCallback(
    () => (
      <>
        {!commentsThreadId && (
          <FooterButton
            onClick={() => {
              setIsEdit(false);
              setShowReplyForm((prev) => !prev);
              setReplyFormVisible?.((prev) => !prev);
            }}
          >
            <ReplyIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Reply')}</FooterButtonText>
          </FooterButton>
        )}

        {currentUserId === reply.author.id && (
          <FooterButton
            onClick={() => {
              setIsEdit(true);
              setShowReplyForm((prev) => !prev);
              setReplyFormVisible?.((prev) => !prev);
            }}
          >
            <EditIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Edit')}</FooterButtonText>
          </FooterButton>
        )}

        {deleteComment && currentUserId === reply.author.id && (
          <FooterButton onClick={() => setShowDeleteConfirmation(true)}>
            <DeleteIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Delete')}</FooterButtonText>
          </FooterButton>
        )}

        {!!(onMarkAsTask || onTaskSave) && (
          <FooterButton
            onClick={() =>
              onMarkAsTask ? onMarkAsTask(reply.message) : setShowTaskForm((prev) => !prev)
            }
          >
            <MarkAsTaskIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Mark as task')}</FooterButtonText>
          </FooterButton>
        )}
      </>
    ),
    [
      commentsThreadId,
      currentUserId,
      deleteComment,
      onMarkAsTask,
      onTaskSave,
      reply.message,
      reply.author.id,
      setReplyFormVisible,
      t,
    ],
  );

  return (
    <>
      <Container
        ref={highlightRef}
        style={{
          borderRadius: '8px',
          boxShadow: isHighlighted
            ? `0 0 0 2px ${theme.palette.accent.main}`
            : '0 0 0 2px transparent',
          transition: 'box-shadow 1s ease-in-out',
        }}
      >
        <Avatar
          sx={{
            width: '22px',
            height: '22px',
            minWidth: '22px',
            minHeight: '22px',
            backgroundColor: avatarColor,
          }}
          userId={author?.id ?? null}
          name={`${author.firstName} ${author.lastName}`}
        />

        <ContentSection>
          <CommentHeader>
            <HeaderSection>
              <UserName>{`${author.firstName} ${author.lastName}`}</UserName>
              <AuthorChips platformChipInfo={platformChipInfo} authorRole={authorRole} />
              <TimeAgo>{dayjs(reply.created).fromNow()}</TimeAgo>
            </HeaderSection>
          </CommentHeader>

          <BodyText>{renderBody()}</BodyText>

          {!!reply.files?.length && downloadCommentFile && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {reply.files
                .filter((f: { objectId?: string | null }) => f.objectId)
                .map((file: { objectId?: string | null; name?: string | null }) => (
                  <CommentReplyAttachmentThumb
                    key={file.objectId}
                    file={file}
                    resourceId={reply.resourceId}
                    downloadCommentFile={downloadCommentFile}
                  />
                ))}
            </Box>
          )}

          <ContentFooter>{renderButtons()}</ContentFooter>

          {(showReplyForm || showTaskForm) && (
            <ContentFooter sx={{ width: '100%' }}>
              <CommentOrTaskForm
                type={showReplyForm ? 'comment' : 'task'}
                greenVariant={showReplyForm}
                comment={reply.message}
                buttonLabel={t(showTaskForm ? 'Add task' : isEdit ? 'Save changes' : 'Add reply')}
                isReply={showReplyForm}
                isEdit={isEdit}
                commentId={reply.id}
                parentId={reply.parentId}
                onTaskSave={onTaskSave}
                onCommentSave={onCommentSave}
                onCancel={() => {
                  setShowReplyForm(false);
                  setShowTaskForm(false);
                  setReplyFormVisible?.((prev) => !prev);
                }}
              />
            </ContentFooter>
          )}
        </ContentSection>
      </Container>

      {showDeleteConfirmation && (
        <ConfirmationDialog
          open={showDeleteConfirmation}
          handleClose={() => setShowDeleteConfirmation(false)}
          handleConfirm={() => {
            deleteComment?.(reply.id);
            setShowDeleteConfirmation(false);
          }}
          title={t('Delete comment')}
          description={t('Are you sure you want to delete this comment?')}
          cancelLabel={t('No')}
        />
      )}
    </>
  );
};

export default CommentReply;
